/**
 * RECON RUNS: every dig the room has run, grouped by what it was aimed at -
 * an item, a lens, a table, or the whole room - with how often, what it cost
 * and what it brought back. Each row goes to what was dug into; opened, it
 * lists its runs one by one. Built on the server from the runs themselves
 * (`boardroom.recon_runs`).
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import type {
  BoardroomReconRunsRow,
  BoardroomReconRuns,
  BoardroomReportKey,
  BoardroomRunFindings,
} from '../../../types';
import { KIND_LABELS } from '../kinds';
import { feedTime, feedTimeTitle } from '../feedTime';
import { useShowInternals } from '../../../contexts/InternalsContext';
import AdminPill, { ADMIN_TEXT } from '../../common/AdminPill';
import { RUN_OPTIONS } from '../intents';
import { strengthWords } from './answerStance';

const AIM_WORDS: Record<BoardroomReconRunsRow['kind'], string> = {
  item: 'item',
  lens: 'lens',
  table: 'table',
  room: 'whole room',
};

type Filter = 'all' | BoardroomReconRunsRow['kind'];

/** "121 evidence · 103 features · 39 pricing", the biggest first. Pure. */
export function addedWords(added: Record<string, number>, limit = 4): string {
  const parts = Object.entries(added ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([k, n]) => `${n} ${(KIND_LABELS[k as keyof typeof KIND_LABELS] ?? k).toLowerCase()}`);
  return parts.join(' · ') || 'nothing yet';
}

/** "3.45M", "446k", "900". Pure. */
export function tokenWords(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

/** How much of its budget a dig used, or several digs together: "64%". Pure. */
export function budgetUsed(runs: { tokens: number; budget: number }[]): string {
  const budget = runs.reduce((n, r) => n + (r.budget || 0), 0);
  if (!budget) return '-';
  const used = runs.reduce((n, r) => n + (r.tokens || 0), 0);
  return `${Math.round((100 * used) / budget)}%`;
}

/** A dig's aim in the words of the button that starts it. */
function intentWords(intent: string): string {
  return RUN_OPTIONS.find(o => o.intent === intent)?.label ?? intent.toLowerCase();
}

/** How many of one kind a dig's list shows before "show all". */
const FOUND_SHOWN = 40;

/** Everything one dig found, by kind, each one a link. */
export function RunFindings({
  findings,
  onOpen,
}: {
  findings: BoardroomRunFindings;
  onOpen: (itemId: number) => void;
}) {
  const [all, setAll] = useState<Record<string, boolean>>({});
  if (!findings.groups.length) return <p className="text-gray-400">It found nothing new.</p>;
  return (
    <div className="flex flex-col gap-2" data-testid={`recon-findings-${findings.run_id}`}>
      {findings.groups.map(g => {
        const shown = all[g.kind] ? g.items : g.items.slice(0, FOUND_SHOWN);
        return (
          <div key={g.kind}>
            <h5 className="mb-0.5 text-[11px] uppercase tracking-wide text-gray-400">
              {KIND_LABELS[g.kind] ?? g.kind} · {g.items.length}
            </h5>
            <ul className="flex flex-wrap gap-1">
              {shown.map(it => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(it.id)}
                    title={it.status === 'merged' || it.status === 'killed' ? `${it.title} (since ${it.status === 'killed' ? 'ruled out' : 'merged'})` : it.title}
                    className={`max-w-[22rem] truncate rounded-full border px-1.5 py-0.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      it.status === 'merged' || it.status === 'killed'
                        ? 'border-dashed border-gray-300 text-gray-400 line-through dark:border-gray-600'
                        : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
                    }`}
                  >
                    {it.title}
                  </button>
                </li>
              ))}
              {g.items.length > FOUND_SHOWN && (
                <li>
                  <button
                    type="button"
                    onClick={() => setAll(a => ({ ...a, [g.kind]: !a[g.kind] }))}
                    className="px-1.5 py-0.5 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                  >
                    {all[g.kind] ? 'show fewer' : `show all ${g.items.length}`}
                  </button>
                </li>
              )}
            </ul>
          </div>
        );
      })}
      {findings.truncated && (
        <p className="text-xs text-gray-400">The first {findings.total} shown; the counts above have the rest.</p>
      )}
    </div>
  );
}

export default function ReconRunsReport({
  data,
  onOpen,
  onLens,
  onTable,
  loadFindings,
  focusRun,
}: {
  data: BoardroomReconRuns;
  onOpen: (itemId: number) => void;
  /** Put a lens row's lens back on. */
  onLens?: (tags: string[]) => void;
  /** Open a table row's table. */
  onTable?: (key: BoardroomReportKey) => void;
  /** Everything one dig found, for a dig opened in the list. */
  loadFindings?: (runId: number) => Promise<BoardroomRunFindings>;
  /** A dig to open on arrival - "see everything it found" on an item's page.
   *  `at` makes a second click on the same dig open it again. */
  focusRun?: { runId: number; at: number } | null;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [openRun, setOpenRun] = useState<number | null>(null);
  const [found, setFound] = useState<Record<number, BoardroomRunFindings | 'loading' | 'failed'>>({});
  const focused = useRef<HTMLTableRowElement | null>(null);
  // Acted on once: the table reloads during a dig, and must not keep jumping.
  const handled = useRef<number | null>(null);
  const focusId = focusRun?.runId ?? null;

  const showRun = (runId: number) => {
    setOpenRun(o => (o === runId ? null : runId));
    if (!loadFindings || found[runId]) return;
    setFound(f => ({ ...f, [runId]: 'loading' }));
    loadFindings(runId)
      .then(r => setFound(f => ({ ...f, [runId]: r })))
      .catch(() => setFound(f => ({ ...f, [runId]: 'failed' })));
  };

  // ARRIVING FROM A PAGE'S "see everything it found": open that dig's row and
  // its findings, and bring it into view.
  useEffect(() => {
    if (!focusRun || handled.current === focusRun.at) return;
    const row = data.rows.find(r => r.runs.some(run => run.id === focusRun.runId));
    if (!row) return;
    handled.current = focusRun.at;
    setFilter('all');
    setOpen(row.key);
    if (openRun !== focusRun.runId) showRun(focusRun.runId);
    window.setTimeout(() => focused.current?.scrollIntoView?.({ block: 'center' }), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRun, data]);
  const rows = data.rows.filter(r => filter === 'all' || r.kind === filter);
  // HOW THE MACHINE RAN - turns, raw tokens, dig numbers - is for admins.
  // Everyone sees what each dig found and how much of its budget it used.
  const internals = useShowInternals();
  const kinds = (['item', 'lens', 'table', 'room'] as const).filter(k => data.rows.some(r => r.kind === k));

  const go = (r: BoardroomReconRunsRow) => {
    if (r.kind === 'item' && r.item_id) onOpen(r.item_id);
    else if (r.kind === 'lens' && r.tags && onLens) onLens(r.tags);
    else if (r.kind === 'table' && r.tables?.[0] && onTable) onTable(r.tables[0] as BoardroomReportKey);
  };

  return (
    <div className="min-w-0 text-xs" data-testid="recon-runs">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span data-testid="recon-runs-totals">
          {data.totals.runs} run{data.totals.runs === 1 ? '' : 's'} on {data.totals.aims} thing
          {data.totals.aims === 1 ? '' : 's'}
          {internals && <span className={ADMIN_TEXT}> · {tokenWords(data.totals.tokens)} tokens billed</span>}
        </span>
        <span className="ml-auto flex gap-1">
          {(['all', ...kinds] as Filter[]).map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={`rounded px-1.5 py-0.5 ${
                filter === k
                  ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid={`recon-runs-filter-${k}`}
            >
              {k === 'all' ? 'all' : AIM_WORDS[k]}
            </button>
          ))}
        </span>
      </div>
      <table className="w-full">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <tr>
            <th className="py-1 text-left font-normal">Dug into</th>
            <th className="py-1 text-right font-normal">Runs</th>
            <th className="py-1 text-left font-normal pl-3">Last</th>
            <th className="py-1 text-right font-normal" title="How much of each dig's budget it spent">
              Budget used
            </th>
            {internals && (
              <th className="py-1 pl-2 text-right font-normal">
                <AdminPill className="normal-case tracking-normal" /> Tokens
              </th>
            )}
            <th className="py-1 text-left font-normal pl-3">What it found</th>
            <th className="py-1 text-left font-normal pl-3">How the last ended</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <Fragment key={r.key}>
              <tr className="border-t border-gray-100 align-top dark:border-gray-700/60" data-testid={`recon-row-${r.key}`}>
                <td className="py-1 pr-2">
                  <div className="flex items-start gap-1.5">
                    <button
                      type="button"
                      onClick={() => setOpen(o => (o === r.key ? null : r.key))}
                      className="icon-btn shrink-0 text-gray-400 hover:text-gray-600"
                      aria-label={open === r.key ? `Hide the runs on ${r.label}` : `Show the runs on ${r.label}`}
                      data-testid={`recon-expand-${r.key}`}
                    >
                      {open === r.key ? '▾' : '▸'}
                    </button>
                    <span className="shrink-0 rounded bg-gray-100 px-1 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {r.kind === 'item' && r.item_kind ? KIND_LABELS[r.item_kind] : AIM_WORDS[r.kind]}
                    </span>
                    {r.kind === 'room' ? (
                      <span className="text-gray-900 dark:text-gray-100">{r.label}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => go(r)}
                        className="min-w-0 text-left text-gray-900 hover:underline dark:text-gray-100"
                        data-testid={`recon-go-${r.key}`}
                      >
                        {r.label}
                      </button>
                    )}
                  </div>
                  {r.answer && (
                    <div className="mt-0.5 pl-8 text-xs text-gray-500 dark:text-gray-400">
                      Answer ({strengthWords(r.answer.strength)}): {r.answer.stance}
                    </div>
                  )}
                </td>
                <td className="py-1 text-right tabular-nums">{r.count}</td>
                <td className="py-1 pl-3 whitespace-nowrap text-gray-500 dark:text-gray-400" title={feedTimeTitle(r.last_at)}>
                  {r.running ? <span className="text-emerald-600">running</span> : feedTime(r.last_at)}
                </td>
                <td className="py-1 text-right tabular-nums">{budgetUsed(r.runs)}</td>
                {internals && (
                  <td className={`py-1 pl-2 text-right tabular-nums ${ADMIN_TEXT}`} title={`${r.tokens.toLocaleString()} tokens billed`}>
                    {tokenWords(r.tokens)}
                  </td>
                )}
                <td className="py-1 pl-3 text-gray-600 dark:text-gray-300">{addedWords(r.added)}</td>
                <td className="py-1 pl-3 text-gray-500 dark:text-gray-400">{r.last_ended ?? '-'}</td>
              </tr>
              {open === r.key &&
                r.runs.map(run => (
                  <Fragment key={run.id}>
                  <tr
                    ref={run.id === focusId ? focused : undefined}
                    className={`text-xs text-gray-500 dark:text-gray-400 ${run.id === focusId ? 'bg-emerald-50/60 dark:bg-emerald-900/10' : ''}`}
                    data-testid={`recon-run-${run.id}`}
                  >
                    <td className="py-0.5 pl-8 pr-2">
                      <div className="flex items-start gap-1.5">
                        {loadFindings && (
                          <button
                            type="button"
                            onClick={() => showRun(run.id)}
                            className="icon-btn shrink-0 text-gray-400 hover:text-gray-600"
                            aria-label={openRun === run.id ? `Hide what dig #${run.id} found` : `Show what dig #${run.id} found`}
                            data-testid={`recon-run-expand-${run.id}`}
                          >
                            {openRun === run.id ? '▾' : '▸'}
                          </button>
                        )}
                        <div>
                          {internals && <span className={ADMIN_TEXT}>#{run.id} · </span>}
                          {intentWords(run.intent)}
                          {run.directive && <div className="line-clamp-2 text-gray-400">{run.directive}</div>}
                        </div>
                      </div>
                    </td>
                    <td className={`py-0.5 text-right tabular-nums ${ADMIN_TEXT}`}>{internals ? `${run.turns} turns` : ''}</td>
                    <td className="py-0.5 pl-3 whitespace-nowrap" title={feedTimeTitle(run.at)}>{feedTime(run.at)}</td>
                    <td className="py-0.5 text-right tabular-nums">{budgetUsed([run])}</td>
                    {internals && (
                      <td
                        className={`py-0.5 pl-2 text-right tabular-nums ${ADMIN_TEXT}`}
                        title={`${run.tokens.toLocaleString()} of ${run.budget.toLocaleString()} budget`}
                      >
                        {tokenWords(run.tokens)}
                      </td>
                    )}
                    <td className="py-0.5 pl-3">
                      {addedWords(run.added)}
                      {/* A DIG THAT STRAYED: inside "not an EHR", it filed EHRs. */}
                      {!!run.outside_lens && (
                        <div className="text-amber-700 dark:text-amber-300" data-testid={`recon-run-outside-${run.id}`}>
                          {run.outside_lens} outside the lens:{' '}
                          {(run.outside_examples ?? []).map((x, i) => (
                            <span key={x.id}>
                              {i > 0 && ', '}
                              <button type="button" onClick={() => onOpen(x.id)} className="hover:underline">
                                {x.title}
                              </button>
                            </span>
                          ))}
                          {run.outside_lens > (run.outside_examples ?? []).length && ' …'}
                        </div>
                      )}
                    </td>
                    <td className="py-0.5 pl-3">{run.ended ?? run.status}</td>
                  </tr>
                  {openRun === run.id && (
                    <tr data-testid={`recon-run-found-${run.id}`}>
                      <td colSpan={internals ? 7 : 6} className="pb-2 pl-14 pr-2 pt-1">
                        {found[run.id] === 'loading' || found[run.id] === undefined ? (
                          <span className="text-gray-400">Reading what it found…</span>
                        ) : found[run.id] === 'failed' ? (
                          <span className="text-red-600 dark:text-red-400">Could not load what it found.</span>
                        ) : (
                          <RunFindings findings={found[run.id] as BoardroomRunFindings} onOpen={onOpen} />
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="py-4 text-gray-400">No runs yet.</p>}
    </div>
  );
}
